import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, ReferenceArea, ReferenceLine, Tooltip } from "recharts";
import api from "@/lib/api";
import { fmtPrice } from "@/lib/format";

function Tip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-md border border-border/70 bg-popover/95 px-2 py-1 shadow-[var(--app-shadow-elev-1)]">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="font-mono tabular text-xs">${fmtPrice(payload[0]?.value)}</div>
    </div>
  );
}

/**
 * MiniPriceChart — compact version used on the landing hero card.
 *
 * Phase 9.HMS.v1 — Accepts an optional `series` prop.
 *   • When `series` is provided (Landing/Homepage flow), the chart
 *     renders it directly. No `/api/stocks/{ticker}/prices` fetch is
 *     performed, so a public visitor cannot cause any FMP call.
 *   • When `series` is omitted (analysis pages), the previous fetch
 *     behavior is preserved for backwards compatibility.
 */
export default function MiniPriceChart({ ticker, fairValue, fairLow, fairHigh, height, range = "1Y", color = "#DDE1E4", series: externalSeries }) {
  const [series, setSeries] = useState(() =>
    Array.isArray(externalSeries)
      ? externalSeries.map((p) => ({ t: p.date || p.t, price: p.close ?? p.price }))
      : []
  );

  useEffect(() => {
    // If the parent supplied a sparkline series (Homepage snapshot),
    // trust it and never fetch — the Homepage must be strictly
    // cache-only for chart data.
    if (Array.isArray(externalSeries)) {
      setSeries(externalSeries.map((p) => ({ t: p.date || p.t, price: p.close ?? p.price })));
      return () => {};
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/stocks/${ticker}/prices`, { params: { range } });
        if (!cancelled) setSeries((res.data.series || []).map((p) => ({ t: p.date, price: p.close })));
      } catch {
        if (!cancelled) setSeries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker, range, externalSeries]);

  const xFmt = useMemo(() => (v) => (v ? v.slice(0, 7) : ""), []);

  return (
    <div style={height ? { height } : undefined} className={height ? "" : "w-full h-full"} aria-label={`${ticker} mini price chart`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="miniGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.20} />
              <stop offset="100%" stopColor={color} stopOpacity={0.0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="t" hide tickFormatter={xFmt} />
          <YAxis hide domain={["auto", "auto"]} />
          <Tooltip content={<Tip />} cursor={{ stroke: "#3A8573", strokeOpacity: 0.35 }} />
          {fairLow && fairHigh ? <ReferenceArea y1={fairLow} y2={fairHigh} fill="rgba(63,174,137,0.12)" stroke="rgba(63,174,137,0.25)" ifOverflow="extendDomain" /> : null}
          {fairValue ? <ReferenceLine y={fairValue} stroke="#3FAE89" strokeDasharray="4 4" strokeOpacity={0.9} ifOverflow="extendDomain" /> : null}
          <Area type="monotone" dataKey="price" stroke={color} strokeWidth={1.4} fill="url(#miniGrad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
